<template>
  <div>
    <!-- Tabs for switching between recommendations and considerations -->
    <div class="tabs">
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'recommendations' }" 
        @click="activeTab = 'recommendations'">
        Basic Recommendations
      </button>
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'considerations' }" 
        @click="activeTab = 'considerations'">
        Detailed Considerations
      </button>
    </div>
    
    <!-- Basic Recommendations Tab -->
    <div v-if="activeTab === 'recommendations'">
      <button @click="generateRecommendations" :disabled="recommendationsLoading">
        Generate Recommendations
      </button>
      <div v-if="recommendationsLoading">
        <p>Loading recommendations...</p>
      </div>
      <div v-else-if="recommendations.length">
        <div 
          v-for="(recommendation, index) in recommendations" 
          :key="index" 
          class="card recommendation-card">
          <!-- Item type badge -->
          <div class="item-type-badge recommendation">
            Recommendation
          </div>
          
          <p><strong>Headline:</strong> {{ recommendation.headline }}</p>
          <p><strong>Detail:</strong> {{ recommendation.detail }}</p>
          <p><strong>Action:</strong> {{ recommendation.action }}</p>
          <div class="button-row">
            <button @click="saveRecommendation(index)">Save</button>
            <button @click="deleteRecommendation(index)">Delete</button>
          </div>
        </div>
      </div>
      <div v-else>
        <p>No recommendations available.</p>
      </div>
    </div>
    
    <!-- Detailed Considerations Tab -->
    <div v-if="activeTab === 'considerations'">
      <button @click="generateConsiderations" :disabled="considerationsLoading">
        Generate Detailed Considerations
      </button>
      <div v-if="considerationsLoading">
        <p>Loading detailed considerations...</p>
      </div>
      <div v-else-if="considerations.length">
        <div 
          v-for="(consideration, index) in considerations" 
          :key="index" 
          class="card consideration-card">
          <!-- Item type badge -->
          <div class="item-type-badge consideration">
            Consideration
          </div>
          
          <p><strong>Headline:</strong> {{ consideration.headline }}</p>
          <p><strong>Context:</strong> {{ consideration.context }}</p>
          <p><strong>Detail:</strong> {{ consideration.detail }}</p>
          <p><strong>Missing/Additional Info Required:</strong> {{ consideration.missing_or_additional_required_information }}</p>
          <p v-if="consideration.action"><strong>Action:</strong> {{ consideration.action }}</p>
          <div class="button-row">
            <button @click="saveConsideration(index)">Save</button>
            <button @click="deleteConsideration(index)">Delete</button>
          </div>
        </div>
      </div>
      <div v-else>
        <p>No detailed considerations available.</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EntityRecommendations',
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    entity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      recommendations: [],
      considerations: [],
      recommendationsLoading: false,
      considerationsLoading: false,
      activeTab: 'recommendations', // Default to showing recommendations tab
    };
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    }
  },
  methods: {
    async fetchRecommendations() {
      this.recommendationsLoading = true;
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/basic_recommendations/`, {
          headers: this.headers,
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Basic Recommendations:', data);
        
        // Extract recommendations from nested array structure
        try {
          if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
            const recommendationsData = data[0];
            if (recommendationsData.length >= 2 && recommendationsData[0] === "basic_recommendations") {
              const recItems = recommendationsData[1];
              
              // Transform each recommendation to object format
              this.recommendations = recItems.map(item => {
                const rec = {};
                if (Array.isArray(item)) {
                  item.forEach(field => {
                    if (Array.isArray(field) && field.length >= 2) {
                      rec[field[0]] = field[1];
                    }
                  });
                }
                return rec;
              });
            } else {
              // Try the expected format
              if (data.basic_recommendations) {
                this.recommendations = data.basic_recommendations;
              } else {
                console.error('Unexpected data format for recommendations:', data);
                this.recommendations = [];
              }
            }
          } else if (data && data.basic_recommendations) {
            // Standard format
            this.recommendations = data.basic_recommendations;
          } else {
            console.error('Unexpected data format for recommendations:', data);
            this.recommendations = [];
          }
        } catch (error) {
          console.error('Error parsing recommendations data:', error);
          this.recommendations = [];
        }
      } catch (error) {
        console.error('Error fetching basic recommendations:', error);
        this.recommendations = [];
      } finally {
        this.recommendationsLoading = false;
      }
    },
    
    async fetchConsiderations() {
      this.considerationsLoading = true;
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/detailed_considerations/`, {
          headers: this.headers,
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Detailed Considerations:', data);
        
        // Extract considerations from nested array structure
        try {
          if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
            const considerationsData = data[0];
            if (considerationsData.length >= 2 && considerationsData[0] === "detailed_considerations") {
              const consItems = considerationsData[1];
              
              // Transform each consideration to object format
              this.considerations = consItems.map(item => {
                const cons = {};
                if (Array.isArray(item)) {
                  item.forEach(field => {
                    if (Array.isArray(field) && field.length >= 2) {
                      cons[field[0]] = field[1];
                    }
                  });
                }
                return cons;
              });
            } else {
              // Try the expected format
              if (data.detailed_considerations) {
                this.considerations = data.detailed_considerations;
              } else {
                console.error('Unexpected data format for considerations:', data);
                this.considerations = [];
              }
            }
          } else if (data && data.detailed_considerations) {
            // Standard format
            this.considerations = data.detailed_considerations;
          } else {
            console.error('Unexpected data format for considerations:', data);
            this.considerations = [];
          }
        } catch (error) {
          console.error('Error parsing considerations data:', error);
          this.considerations = [];
        }
      } catch (error) {
        console.error('Error fetching detailed considerations:', error);
        this.considerations = [];
      } finally {
        this.considerationsLoading = false;
      }
    },
    
    generateRecommendations() {
      this.fetchRecommendations();
    },
    
    generateConsiderations() {
      this.fetchConsiderations();
    },
    
    saveRecommendation(index) {
      console.log('Save recommendation:', this.recommendations[index]);
      // Implement save functionality here
    },
    
    deleteRecommendation(index) {
      // Remove the recommendation from the list
      const updatedRecommendations = [...this.recommendations];
      updatedRecommendations.splice(index, 1);
      this.recommendations = updatedRecommendations;
    },
    
    saveConsideration(index) {
      console.log('Save consideration:', this.considerations[index]);
      // Implement save functionality here
    },
    
    deleteConsideration(index) {
      // Remove the consideration from the list
      const updatedConsiderations = [...this.considerations];
      updatedConsiderations.splice(index, 1);
      this.considerations = updatedConsiderations;
    }
  },
};
</script>

<style scoped>
.card {
  border: 1px solid #ccc;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
  border-radius: 4px;
}

.recommendation-card {
  border-left: 4px solid #27ae60; /* Green for recommendations */
  background-color: #f0f9f4;
}

.consideration-card {
  border-left: 4px solid #9b59b6; /* Purple for considerations */
  background-color: #f9f0f9;
}

.item-type-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 4px 8px;
  border-radius: 0 4px 0 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.item-type-badge.recommendation {
  background-color: #27ae60;
}

.item-type-badge.consideration {
  background-color: #9b59b6;
}

.tabs {
  display: flex;
  margin-bottom: 16px;
  border-bottom: 1px solid #ddd;
}

.tab-button {
  padding: 8px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  color: #666;
}

.tab-button.active {
  border-bottom: 2px solid #3498db;
  color: #3498db;
}

.button-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
</style>